import t from "@/lang/lang";
import { Trash2 } from "lucide-react";
import {
  GfsAssessment,
  GfsMeasurePeriodUnit,
} from "@/services/backend/gfs/assessment/types";
import {
  formatGfsConsumableDisplayName,
  GfsConsumable,
} from "@/services/backend/gfs/catalog/types";
import { formatGfsUnit } from "@/services/backend/gfs/unit";
import { IntNumberInput } from "@/shared/components/domain/qpm/inputs/number-input";
import { InputDebounced } from "@/shared/components/inputs/input-debounced";
import { TextareaDebounced } from "@/shared/components/inputs/textarea-debounced";
import { useAssessmentEditorContext } from "@/services/backend/gfs/assessment/components/assessment-editor-context";
import { AssessmentField } from "@/services/backend/gfs/assessment/components/assessment-field";
import { PestSelection } from "@/services/backend/gfs/assessment/components/pest-selection";
import { Card } from "@/shared/components/ui/card";
import { Button } from "@/shared/components/ui/button";
import { Input } from "@/shared/components/ui/input";
import { Checkbox } from "@/shared/components/ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/shared/components/ui/select";
import {
  TimeNumberInput,
  TimeUnit,
} from "@/shared/components/inputs/time-number-input";
import { toast } from "sonner";

const MEASURE_PERIOD_UNITS: {
  value: GfsMeasurePeriodUnit;
  label: string;
}[] = [
  { value: "hours", label: "Stunden" },
  { value: "days", label: "Tage" },
  { value: "weeks", label: "Wochen" },
  { value: "months", label: "Monate" },
];

export function GfsAssessmentAdditionalInfoPage() {
  const {
    draft,
    pests,
    selectedPestIDs,
    derivedPestIDs,
    accumulatedLaborDuration,
    globalLaborDuration,
    workTimeInputResetKey,
    setWorkTimeInputResetKey,
    setManualPestIDs,
    updateAssessment,
    catalogConsumables,
  } = useAssessmentEditorContext();
  return (
    <Card className="space-y-5 p-5">
      <div className="grid gap-4 xl:grid-cols-[16rem_16rem_auto_28rem]">
        <AssessmentField label={t("Befallsstärke")}>
          <Select
            value={draft.infestationLevel || "none"}
            onValueChange={(level) =>
              updateAssessment({
                infestationLevel:
                  level === "none"
                    ? ""
                    : (level as GfsAssessment["infestationLevel"]),
              })
            }
          >
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="none">{t("Nicht angegeben")}</SelectItem>
              <SelectItem value="light">{t("Leicht")}</SelectItem>
              <SelectItem value="medium">{t("Mittel")}</SelectItem>
              <SelectItem value="heavy">{t("Stark")}</SelectItem>
            </SelectContent>
          </Select>
        </AssessmentField>
        <AssessmentField label={t("Fahrzeit")}>
          <TimeNumberInput
            value={draft.workEstimate.travelDuration ?? 0}
            onChange={(travelDuration) => {
              updateAssessment((current) => ({
                workEstimate: {
                  ...current.workEstimate,
                  travelDuration,
                },
              }));
            }}
          />
        </AssessmentField>
        <div className="flex flex-wrap items-end gap-2">
          <AssessmentField label={t("Anzahl Maßnahmen")}>
            <IntNumberInput
              value={draft.workEstimate.measureCount ?? 0}
              onChange={(measureCount) => {
                updateAssessment((current) => ({
                  workEstimate: {
                    ...current.workEstimate,
                    measureCount,
                  },
                }));
              }}
            />
          </AssessmentField>
          <AssessmentField label={t("Zeitraum")}>
            <IntNumberInput
              min={0}
              disabled={!draft.workEstimate.measurePeriodUnit}
              value={draft.workEstimate.measurePeriodValue ?? 0}
              onChange={(measurePeriodValue) => {
                updateAssessment((current) => ({
                  workEstimate: {
                    ...current.workEstimate,
                    measurePeriodValue,
                  },
                }));
              }}
            />
          </AssessmentField>
          <AssessmentField label={t("Zeitangabe")}>
            <Select
              value={draft.workEstimate.measurePeriodUnit || "none"}
              onValueChange={(value) => {
                const measurePeriodUnit =
                  value === "none" ? "" : (value as GfsMeasurePeriodUnit);
                updateAssessment((current) => ({
                  workEstimate: {
                    ...current.workEstimate,
                    measurePeriodValue:
                      measurePeriodUnit === ""
                        ? 0
                        : current.workEstimate.measurePeriodValue,
                    measurePeriodUnit,
                  },
                }));
              }}
            >
              <SelectTrigger className="w-44">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="none">{t("Keine Zeitangabe")}</SelectItem>
                {MEASURE_PERIOD_UNITS.map((unit) => (
                  <SelectItem key={unit.value} value={unit.value}>
                    {t(unit.label)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </AssessmentField>
        </div>
        <div className="flex items-end gap-3">
          <label className="flex h-8 items-center gap-2 text-sm font-bold">
            <Checkbox
              checked={draft.workEstimate.eradicationMonitoring ?? false}
              onCheckedChange={(checked) => {
                updateAssessment((current) => ({
                  workEstimate: {
                    ...current.workEstimate,
                    eradicationMonitoring: checked === true,
                    eradicationMonitoringDuration:
                      checked === true
                        ? current.workEstimate.eradicationMonitoringDuration
                        : 0,
                  },
                }));
              }}
            />
            <span>{t("Tilgungskontrolle")}</span>
          </label>
          <AssessmentField label={t("Dauer")}>
            <TimeNumberInput
              disabled={!draft.workEstimate.eradicationMonitoring}
              value={draft.workEstimate.eradicationMonitoringDuration ?? 0}
              onChange={(eradicationMonitoringDuration) => {
                updateAssessment((current) => ({
                  workEstimate: {
                    ...current.workEstimate,
                    eradicationMonitoringDuration,
                  },
                }));
              }}
            />
          </AssessmentField>
        </div>
      </div>
      <div className="flex flex-wrap items-end gap-2 border-t pt-5">
        <AssessmentField label={t("Arbeitszeit aus Bereichen")}>
          <TimeNumberInput
            disabled
            timeUnit={TimeUnit.Minutes}
            value={accumulatedLaborDuration}
          />
        </AssessmentField>
        <AssessmentField label={t("Arbeitszeit gesamt")}>
          <TimeNumberInput
            key={workTimeInputResetKey}
            value={globalLaborDuration}
            onChange={(duration) => {
              if (duration < accumulatedLaborDuration) {
                toast.error(t("Ungültige Arbeitszeit"), {
                  description: t(
                    "Arbeitszeit gesamt darf nicht kleiner als die Arbeitszeit der Bereiche sein.",
                  ),
                });
                setWorkTimeInputResetKey((key) => key + 1);
                return;
              }

              updateAssessment((current) => ({
                workEstimate: {
                  ...current.workEstimate,
                  duration,
                },
              }));
            }}
          />
        </AssessmentField>
      </div>
      <AssessmentField label={t("Allgemeine Anmerkungen")}>
        <TextareaDebounced
          value={draft.description ?? ""}
          onChange={(event) =>
            updateAssessment({ description: event.target.value })
          }
        />
      </AssessmentField>
      <AssessmentConsumables
        consumables={catalogConsumables}
        materials={draft.materials ?? []}
        onChange={(materials) => updateAssessment({ materials })}
      />
      <PestSelection
        pests={pests}
        selected={selectedPestIDs}
        derived={derivedPestIDs}
        onChange={setManualPestIDs}
      />
    </Card>
  );
}

function AssessmentConsumables({
  consumables,
  materials,
  onChange,
}: {
  consumables: GfsConsumable[];
  materials: GfsAssessment["materials"];
  onChange: (materials: GfsAssessment["materials"]) => void;
}) {
  const update = (
    index: number,
    patch: Partial<GfsAssessment["materials"][number]>,
  ) =>
    onChange(
      materials.map((material, currentIndex) =>
        currentIndex === index ? { ...material, ...patch } : material,
      ),
    );
  const add = (id: string) => {
    const consumable = consumables.find((item) => item.id === id);
    if (!consumable) return;
    const existing = materials.findIndex(
      (material) => material.sourceId === id,
    );
    if (existing >= 0) {
      update(existing, { quantity: materials[existing].quantity + 1 });
      return;
    }
    onChange([
      ...materials,
      {
        sourceId: consumable.id,
        key: consumable.id,
        name: consumable.name,
        unit: consumable.unit,
        quantity: 1,
        unitPrice: consumable.unitPrice,
        productDisplayText: consumable.productDisplayText,
        takenAt: new Date().toISOString(),
      },
    ]);
  };
  return (
    <div className="space-y-3 border-t pt-5">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-semibold">{t("Verbrauchsmaterialien")}</h3>
        <Select value="" onValueChange={add}>
          <SelectTrigger className="w-64">
            <SelectValue placeholder={t("Verbrauchsmaterial hinzufügen")} />
          </SelectTrigger>
          <SelectContent>
            {consumables.map((consumable) => (
              <SelectItem key={consumable.id} value={consumable.id}>
                {formatGfsConsumableDisplayName(consumable)}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      {materials.map((material, index) => (
        <div
          className="grid gap-2 md:grid-cols-[1fr_7rem_7rem_8rem_1fr_auto]"
          key={`${material.sourceId}-${index}`}
        >
          <span
            className="flex min-h-9 items-center"
            title={formatGfsConsumableDisplayName(material)}
          >
            {formatGfsConsumableDisplayName(material)}
          </span>
          <Input disabled value={formatGfsUnit(material.unit)} />
          <InputDebounced
            type="number"
            min="0"
            value={material.quantity}
            onChange={(event) =>
              update(index, { quantity: Number(event.target.value) })
            }
          />
          <InputDebounced
            type="number"
            min="0"
            value={material.unitPrice}
            onChange={(event) =>
              update(index, { unitPrice: Number(event.target.value) })
            }
          />
          <TextareaDebounced
            className="min-h-9"
            placeholder={t("Bemerkung")}
            value={material.comment ?? ""}
            onChange={(event) => update(index, { comment: event.target.value })}
          />
          <Button
            size="icon"
            variant="ghost"
            onClick={() =>
              onChange(
                materials.filter(
                  (_material, currentIndex) => currentIndex !== index,
                ),
              )
            }
          >
            <Trash2 className="h-4 w-4" />
          </Button>
        </div>
      ))}
      {materials.length === 0 && (
        <p className="text-muted-foreground text-sm">
          {t("Keine Verbrauchsmaterialien")}
        </p>
      )}
    </div>
  );
}
